import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { models } from 'powerbi-client';
import { useReport } from 'powerbi-report-component';
import { ElementType, useContext, useEffect, useRef, useState } from 'react';
import {
  FiBookmark,
  FiInfo,
  FiMaximize2,
  FiMinimize2,
  FiMoreVertical,
  FiPrinter,
  FiRefreshCw,
  FiStar,
} from 'react-icons/fi';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';

import { DeviceContext } from '../../../../contexts/DeviceContext';
import { useAuth } from '../../../../hooks/auth';
import { BI_CATALOG_KEY, useToggleBiFavorite } from '../../../../hooks/useBi';
import {
  BiReport,
  BiView,
  biErrorMessage,
  biService,
} from '../../../../services/biService';
import { parseBranch } from '../../../../utils/biAccess';
import reportProps from '../../../../utils/reportProps';
import { apllyToast } from '../../../Global/Toast2.0';
import { ReportInfoDrawer } from '../../../_components_0.2/BI/ReportInfoDrawer';
import { DrawerNavigationBi } from '../../../_components_0.2/Drawer/DrawerNavigationBi';
import { ModalAddNewBookmarkBI } from '../../../_components_0.2/Modal/ModalAddNewBookmarkBI';
import { BiPage, PaginationBI } from '../../../_components_0.2/PaginationBI';

// Renova o token alguns minutos antes de vencer. A API devolve token novo
// quando faltam 10 minutos ou menos (tokenValidity.ts no samasc-api).
// Renova 3 minutos antes de vencer; a API regera o token quando faltam 4
// minutos ou menos (tokenValidity.ts no samasc-api).
const TOKEN_REFRESH_BEFORE_MS = 3 * 60 * 1000;
const TOKEN_RETRY_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;
// Se o Power BI não terminar de carregar nesse tempo, mostra o erro em vez
// de deixar o carregamento girando para sempre.
const LOAD_TIMEOUT_MS = 90 * 1000;

type Status = 'loading' | 'ready' | 'error';

interface PowerBiPage {
  name: string;
  displayName: string;
  isActive: boolean;
  visibility: number;
}

interface PowerBiFilter {
  target?: { table?: string; column?: string };
  values?: unknown[];
}

interface Props {
  report: BiReport;
}

interface Action {
  key: string;
  label: string;
  icon: ElementType;
  onClick: () => void;
  color?: string;
  iconFill?: string;
}

function branchFilter(branch: number) {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: { table: 'filial', column: 'filial' },
    operator: 'In',
    values: [branch],
  };
}

function isBranchFilter(filter: PowerBiFilter) {
  return (
    filter.target?.table === 'filial' && filter.target?.column === 'filial'
  );
}

export const ReportBIManagement = ({ report }: Props) => {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { window: layoutType } = useContext(DeviceContext);
  const toggleFavorite = useToggleBiFavorite();
  const isWideVersion = useBreakpointValue({ base: false, md: true });

  const viewsDrawer = useDisclosure();
  const infoDrawer = useDisclosure();
  const bookmarkModal = useDisclosure();

  const [embed, setEmbed] = useReport();
  const reportRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [pages, setPages] = useState<BiPage[]>([]);
  const [activePage, setActivePage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [capturedBookmark, setCapturedBookmark] = useState('');
  const [attempt, setAttempt] = useState(0);

  // Timers e listeners do Power BI rodam fora do ciclo do React: leem sempre
  // o valor mais recente pelas refs.
  const embedRef = useRef<any>(null);
  const mountedRef = useRef(true);
  const statusRef = useRef<Status>('loading');
  const reportDataRef = useRef(report);
  const expiryRef = useRef(0);
  const timerRef = useRef<number>();
  const accessLoggedRef = useRef(false);
  const initialPageRef = useRef(
    new URLSearchParams(location.search).get('page') || undefined,
  );

  reportDataRef.current = report;
  statusRef.current = status;

  const branch = parseBranch(user?.access_of_sector?.branch);
  const mustFilterBranch = !user?.flag_4;
  // Ao abrir a página direto (F5, link), o usuário vem do navegador sem o
  // setor; ele chega logo depois, de /accesses/show. Até lá (`undefined`) o
  // relatório espera em vez de acusar "sem filial".
  const sectorLoaded = user?.access_of_sector !== undefined;
  const activePageName =
    pages.find(page => page.name === activePage)?.displayName || '';

  const fail = (message: string) => {
    setErrorMessage(message);
    setStatus('error');
  };

  const clearTokenTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const refreshToken = async () => {
    clearTokenTimer();

    try {
      const token = await biService.getEmbedToken(reportDataRef.current);

      if (embedRef.current) {
        await embedRef.current.setAccessToken(token.accessToken);
      }

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      scheduleTokenRefresh(token.expiry);
    } catch {
      if (mountedRef.current) {
        timerRef.current = window.setTimeout(refreshToken, TOKEN_RETRY_MS);
      }
    }
  };

  const scheduleTokenRefresh = (expiry: string) => {
    clearTokenTimer();

    // Uma renovação que termina depois de sair da página não agenda outra.
    if (!mountedRef.current) {
      return;
    }

    expiryRef.current = new Date(expiry).getTime();

    const remaining = expiryRef.current - Date.now() - TOKEN_REFRESH_BEFORE_MS;
    // Validade inválida não pode virar um laço de pedidos de token.
    const delay = Number.isFinite(remaining)
      ? Math.max(remaining, MIN_REFRESH_DELAY_MS)
      : TOKEN_RETRY_MS;

    timerRef.current = window.setTimeout(refreshToken, delay);
  };

  // Mantém o relatório restrito à filial do usuário, inclusive depois de
  // aplicar uma visualização salva por alguém de outra filial. Nenhum filtro
  // existente é removido (nem os que o autor salvou no relatório): o da filial
  // só é acrescentado, então o resultado é sempre a interseção, como no
  // `updateFilters(Add)` de antes.
  const ensureBranchFilter = async () => {
    const instance = embedRef.current;

    if (!instance || !mustFilterBranch) {
      return;
    }

    const filters: PowerBiFilter[] = await instance.getFilters();
    const alreadyApplied = filters.some(
      filter =>
        isBranchFilter(filter) &&
        filter.values?.length === 1 &&
        filter.values[0] === branch,
    );

    if (!alreadyApplied) {
      await instance.setFilters([...filters, branchFilter(branch)]);
    }
  };

  const updatePageInUrl = (pageName: string) => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('page') === pageName) {
      return;
    }

    params.set('page', pageName);
    history.replace({
      pathname: window.location.pathname,
      search: params.toString(),
    });
  };

  // Embute o relatório (e de novo a cada "Tentar novamente").
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setStatus('loading');
      setErrorMessage('');

      if (mustFilterBranch && !sectorLoaded) {
        return;
      }

      if (mustFilterBranch && !Number.isFinite(branch)) {
        fail(
          'Seu setor não tem filial cadastrada, por isso o relatório não pode ser aberto. Fale com o administrador.',
        );
        return;
      }

      try {
        const token = await biService.getEmbedToken(report);

        if (cancelled || !reportRef.current) {
          return;
        }

        const { initialReportProps } = reportProps({
          accessToken: token.accessToken,
          embedUrl: report.embededUrl,
          embedId: report.embededId,
          window: layoutType,
          activeEditorBI: !!user?.flag_5,
          activeViewFiltersBI: !!user?.flag_6,
          pageName: initialPageRef.current,
        });

        if (attempt > 0) {
          try {
            (window as any).powerbi?.reset(reportRef.current);
          } catch {
            // Nada embutido ainda: a tentativa anterior falhou antes do embed.
          }
        }

        setEmbed(reportRef, initialReportProps as any);
        scheduleTokenRefresh(token.expiry);
      } catch {
        // A API responde com mensagens técnicas ("Campo obrigatório não
        // informado: accessToken") quando o login no Power BI falha: o usuário
        // vê um texto claro e o detalhe fica no log da API.
        if (!cancelled) {
          fail(
            'Não foi possível autorizar a abertura do relatório no Power BI. Tente novamente em instantes ou fale com o administrador.',
          );
        }
      }
    }

    load();

    return () => {
      cancelled = true;
      clearTokenTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.id, attempt, sectorLoaded]);

  useEffect(() => {
    if (status !== 'loading') {
      return undefined;
    }

    const timeout = window.setTimeout(
      () =>
        fail(
          'O Power BI demorou demais para abrir o relatório. Tente novamente.',
        ),
      LOAD_TIMEOUT_MS,
    );

    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, attempt]);

  // Eventos do relatório embutido.
  useEffect(() => {
    if (!embed) {
      return undefined;
    }

    embedRef.current = embed;

    async function handleLoaded() {
      let loadedPages: PowerBiPage[] = [];

      try {
        loadedPages = await embed.getPages();
      } catch {
        loadedPages = [];
      }

      const current = loadedPages.find(page => page.isActive);

      setPages(
        loadedPages
          .filter(
            page => page.visibility === models.SectionVisibility.AlwaysVisible,
          )
          .map(page => ({ name: page.name, displayName: page.displayName })),
      );
      setActivePage(current?.name || '');

      try {
        await ensureBranchFilter();
      } catch {
        fail(
          'Não foi possível aplicar o filtro da sua filial neste relatório. Fale com o administrador.',
        );
        return;
      }

      setStatus('ready');

      if (!accessLoggedRef.current) {
        accessLoggedRef.current = true;
        biService
          .registerAccess(reportDataRef.current.id, current?.displayName)
          .then(() => queryClient.invalidateQueries(BI_CATALOG_KEY))
          .catch(() => undefined);
      }
    }

    function handlePageChanged(event: { detail?: { newPage?: PowerBiPage } }) {
      const page = event.detail?.newPage;

      if (page) {
        setActivePage(page.name);
        updatePageInUrl(page.name);
      }
    }

    function handleError(event: { detail?: { message?: string } }) {
      if (event.detail?.message === 'TokenExpired') {
        refreshToken();
        return;
      }

      // Com `hideErrors` o Power BI não mostra nada: um erro antes do
      // `loaded` (relatório removido, sem acesso, página inexistente) deixaria
      // o carregamento girando para sempre.
      if (statusRef.current === 'loading') {
        fail(
          'Não foi possível abrir o relatório no Power BI. Tente novamente ou consulte o administrador.',
        );
      }
    }

    embed.on('loaded', handleLoaded);
    embed.on('pageChanged', handlePageChanged);
    embed.on('error', handleError);

    return () => {
      embed.off('loaded');
      embed.off('pageChanged');
      embed.off('error');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embed]);

  // Com a aba em segundo plano o navegador atrasa os timers: ao voltar,
  // renova na hora se o token estiver perto de vencer.
  useEffect(() => {
    function handleVisibility() {
      if (
        document.visibilityState === 'visible' &&
        expiryRef.current &&
        expiryRef.current - Date.now() < TOKEN_REFRESH_BEFORE_MS
      ) {
        refreshToken();
      }
    }

    function handleFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    }

    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    const element = reportRef.current;

    return () => {
      mountedRef.current = false;
      clearTokenTimer();

      // Desmonta o embed: senão cada relatório aberto fica em
      // window.powerbi.embeds até recarregar a página.
      try {
        (window as any).powerbi?.reset(element);
      } catch {
        // Nada embutido.
      }
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const views = useQuery(
    ['bi-views', report.embededId, user?.id],
    () => biService.listViews(user.id, report.embededId),
    { enabled: viewsDrawer.isOpen },
  );

  // Drawers e modais abrem fora do container em tela cheia: sai dela antes.
  const exitFullscreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
    }
  };

  const handleToggleFullscreen = async () => {
    if (document.fullscreenElement) {
      await exitFullscreen();
      return;
    }

    if (containerRef.current?.requestFullscreen) {
      await containerRef.current.requestFullscreen().catch(() => undefined);
      return;
    }

    // Navegadores sem Fullscreen API em elementos (iPhone): usa a do Power BI.
    try {
      embedRef.current?.fullscreen();
    } catch {
      apllyToast('info', 'Tela cheia não disponível neste navegador.');
    }
  };

  const handleReload = async () => {
    // Cobre o relatório até o `loaded` reaplicar o filtro da filial: o
    // reload do Power BI volta aos filtros salvos no relatório.
    setStatus('loading');

    try {
      await embedRef.current?.reload();
    } catch {
      fail('Não foi possível recarregar o relatório.');
    }
  };

  const handlePrint = async () => {
    try {
      await embedRef.current?.print();
    } catch {
      apllyToast('error', 'Não foi possível imprimir o relatório.');
    }
  };

  const handleRetry = () => {
    // A página da URL pode ser a causa do erro (foi removida do relatório).
    initialPageRef.current = undefined;
    setAttempt(value => value + 1);
  };

  const handleSelectPage = async (pageName: string) => {
    const previous = activePage;
    setActivePage(pageName);

    try {
      await embedRef.current?.setPage(pageName);
    } catch {
      setActivePage(previous);
      apllyToast('error', 'Não foi possível abrir esta página.');
    }
  };

  const handleOpenViews = async () => {
    await exitFullscreen();
    viewsDrawer.onOpen();
  };

  const handleOpenInfo = async () => {
    await exitFullscreen();
    infoDrawer.onOpen();
  };

  const handleSaveCurrentView = async () => {
    try {
      const captured = await embedRef.current.bookmarksManager.capture({
        personalizeVisuals: true,
      });

      setCapturedBookmark(captured.state);
      viewsDrawer.onClose();
      bookmarkModal.onOpen();
    } catch {
      apllyToast('error', 'Não foi possível capturar a visualização.');
    }
  };

  const handleCreateView = async (name: string, isPublic: boolean) => {
    try {
      await biService.createView({
        bookmark: capturedBookmark,
        name,
        access_id: user.id,
        embedId: report.embededId,
        public: isPublic,
      });
      await views.refetch();
      apllyToast('success', 'Visualização salva.');
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível salvar a visualização.'),
      );
      throw err;
    }
  };

  const handleApplyView = async (view: BiView) => {
    try {
      await embedRef.current.bookmarksManager.applyState(view.bookmark);
      await ensureBranchFilter();
      viewsDrawer.onClose();
    } catch {
      apllyToast('error', 'Não foi possível aplicar a visualização.');
    }
  };

  const handleRenameView = async (view: BiView, name: string) => {
    try {
      await biService.renameView(view.id, name);
      await views.refetch();
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível renomear a visualização.'),
      );
      throw err;
    }
  };

  const handleDeleteView = async (view: BiView) => {
    try {
      await biService.deleteView(view.id);
      await views.refetch();
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível excluir a visualização.'),
      );
      throw err;
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite.mutate(report, {
      onError: () =>
        apllyToast('error', 'Não foi possível atualizar os favoritos.'),
    });
  };

  const actions: Action[] = [
    {
      key: 'favorite',
      label: report.isFavorite ? 'Remover dos favoritos' : 'Favoritar',
      icon: FiStar,
      onClick: handleToggleFavorite,
      color: report.isFavorite ? 'yellow.400' : undefined,
      iconFill: report.isFavorite ? 'currentColor' : 'none',
    },
    {
      key: 'views',
      label: 'Minhas visualizações',
      icon: FiBookmark,
      onClick: handleOpenViews,
    },
    {
      key: 'info',
      label: 'Sobre e comentários',
      icon: FiInfo,
      onClick: handleOpenInfo,
    },
    {
      key: 'reload',
      label: 'Recarregar',
      icon: FiRefreshCw,
      onClick: handleReload,
    },
    {
      key: 'print',
      label: 'Imprimir',
      icon: FiPrinter,
      onClick: handlePrint,
    },
    {
      key: 'fullscreen',
      label: isFullscreen ? 'Sair da tela cheia' : 'Tela cheia',
      icon: isFullscreen ? FiMinimize2 : FiMaximize2,
      onClick: handleToggleFullscreen,
    },
  ];

  const ready = status === 'ready';

  return (
    <Box
      ref={containerRef}
      display="flex"
      flexDirection="column"
      bg={isFullscreen ? 'gray.900' : undefined}
      h={isFullscreen ? '100vh' : undefined}
      p={isFullscreen ? '3' : '0'}
    >
      <Flex align="center" justify="space-between" gridGap="3" mb="3">
        <Text fontSize="sm" color="gray.500" noOfLines={1}>
          {report.groupNameSamasc || 'Sem grupo'}
          {activePageName ? ` · ${activePageName}` : ''}
        </Text>

        {isWideVersion ? (
          <HStack spacing="1">
            {actions.map(action => (
              <Tooltip key={action.key} label={action.label} hasArrow>
                <IconButton
                  aria-label={action.label}
                  icon={<Icon as={action.icon} fill={action.iconFill} />}
                  size="sm"
                  variant="ghost"
                  color={action.color}
                  isDisabled={
                    action.key !== 'info' && action.key !== 'favorite' && !ready
                  }
                  onClick={action.onClick}
                />
              </Tooltip>
            ))}
          </HStack>
        ) : (
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              aria-label="Ações do relatório"
              icon={<FiMoreVertical />}
              size="sm"
              variant="ghost"
            />
            <MenuList bg="gray.800">
              {actions.map(action => (
                <MenuItem
                  key={action.key}
                  icon={<Icon as={action.icon} fill={action.iconFill} />}
                  onClick={action.onClick}
                  isDisabled={
                    action.key !== 'info' && action.key !== 'favorite' && !ready
                  }
                >
                  {action.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        )}
      </Flex>

      {status === 'error' && (
        <Alert status="error" borderRadius="md" mb="3">
          <AlertIcon />
          <AlertDescription flex="1">{errorMessage}</AlertDescription>
          <Button size="sm" onClick={handleRetry}>
            Tentar novamente
          </Button>
        </Alert>
      )}

      <Flex
        direction="column"
        position="relative"
        flex={isFullscreen ? '1' : undefined}
        h={
          isFullscreen ? undefined : { base: '75vh', lg: 'calc(100vh - 220px)' }
        }
        minH="420px"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        overflow="hidden"
        bg="white.100"
        display={status === 'error' ? 'none' : 'flex'}
      >
        <Box
          ref={reportRef}
          flex="1"
          w="100%"
          sx={{ iframe: { border: 'none' } }}
        />

        {status === 'loading' && (
          <Flex
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            align="center"
            justify="center"
            bg="white.100"
          >
            <Spinner size="xl" color="blue.500" />
          </Flex>
        )}

        <PaginationBI
          pages={pages}
          activePage={activePage}
          onSelectPage={handleSelectPage}
        />
      </Flex>

      <DrawerNavigationBi
        isOpen={viewsDrawer.isOpen}
        onClose={viewsDrawer.onClose}
        views={views.data || []}
        isLoading={views.isLoading}
        currentUserId={user.id}
        onApply={handleApplyView}
        onSaveCurrent={handleSaveCurrentView}
        onRename={handleRenameView}
        onDelete={handleDeleteView}
      />

      <ModalAddNewBookmarkBI
        isOpen={bookmarkModal.isOpen}
        onClose={bookmarkModal.onClose}
        onSave={handleCreateView}
      />

      <ReportInfoDrawer
        report={report}
        isOpen={infoDrawer.isOpen}
        onClose={infoDrawer.onClose}
        currentPage={activePageName}
      />
    </Box>
  );
};
